"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Clock, ArrowRight, MapPin } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const getReadTime = (content: string = "") => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const time = Math.ceil(words / wordsPerMinute);
  return `${time} min read`;
};
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};
function PostItem({
  post,
  index
}: {
  post: any;
  index: number;
}) {
  const rotation = index % 2 === 0 ? "rotate-1" : "-rotate-1";
  const category = post.categories?.[0]?.name || "Adventure";
  const image = post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/placeholder_landscape.png";
  return <div className="break-inside-avoid mb-8" data-index={index}>
      <Card className="bg-card border-none shadow-none overflow-visible group">
        {}
        <div className={`relative bg-stone-100 p-2 shadow-lg transition-transform duration-500 ease-out group-hover:rotate-0 ${rotation}`}>
          <div className="relative aspect-[4/3] overflow-hidden bg-stone-200">
            <img src={image} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          
          {}
          <Badge variant="secondary" className="absolute -top-3 -right-3 z-10 bg-primary text-primary-foreground border-2 border-card shadow-sm px-3 py-1 text-xs uppercase tracking-wider font-bold">
            {category}
          </Badge>
        </div>

        {}
        <div className="pt-6 px-2">
          <Link to={post.link || "#"} className="block group-hover:text-primary transition-colors">
            <h3 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-3 tracking-wide">
              {post.title}
            </h3>
          </Link>
          
          <div className="text-muted-foreground text-sm line-clamp-2 mb-4 leading-relaxed" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />

          {}
          <div className="flex items-center justify-between border-t border-border/50 pt-4 mt-auto">
            <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                {getReadTime(post.content)}
              </span>
            </div>
            
            <Link to={post.link || "#"}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full hover:bg-primary hover:text-primary-foreground">
                <ArrowRight className="w-4 h-4" />
                <span className="sr-only">Read more</span>
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>;
}
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
        {[...Array(6)].map((_, i) => <div key={i} className="break-inside-avoid mb-8" data-index={i}>
            <div className="bg-card rounded-xl p-4 h-96 animate-pulse">
              <div className="bg-muted h-48 w-full mb-4 rounded-sm" />
              <div className="bg-muted h-8 w-3/4 mb-2 rounded-sm" />
              <div className="bg-muted h-4 w-full mb-2 rounded-sm" />
              <div className="bg-muted h-4 w-1/2 rounded-sm" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20 bg-card/50 rounded-xl border border-dashed border-border">
        <p className="text-muted-foreground mb-4">Failed to load the travel log.</p>
        <Button onClick={refetch} variant="outline">Retry Connection</Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-20 bg-card/50 rounded-xl border border-dashed border-border">
        <p className="text-muted-foreground">No adventures recorded yet.</p>
      </div>;
  }
  return <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
      {posts.map((post, i) => <PostItem key={post.id} post={post} index={i} />)}
    </div>;
}
export default function RecentDustTracks() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="recent-dust-tracks" className="dark bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562326/img/topo_pattern_dark.png')] bg-repeat bg-[length:400px_400px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-border/40 pb-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 text-primary font-mono text-sm uppercase tracking-widest mb-2">
              <MapPin className="w-4 h-4" />
              <span>Field Notes</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground">
              Recent Dust & Tracks
            </h2>
            <p className="mt-4 text-muted-foreground text-lg max-w-xl">
              Latest dispatches from the trail. Gear tests, route reports, and campfire stories from the American West.
            </p>
          </div>
          
          <Link to="/TravelLogPage">
            <Button variant="outline" className="hidden md:flex border-primary/50 hover:border-primary text-foreground hover:bg-primary/10">
              View Full Log <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query}>
          <PostsGridConsumer />
        </PostsProvider>

        <div className="mt-12 text-center md:hidden">
          <Link to="/TravelLogPage">
            <Button variant="outline" className="w-full border-primary/50 hover:border-primary">
              View Full Log <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}